import React, { useMemo, useState } from 'react';
import MobileReportOrientationManager from './MobileReportOrientationManager';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart, Line, Sector
} from 'recharts';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { standardizeSourceName } from '../../utils/reportAggregationUtils';
import { ReportData } from '../../types/reportTypes';

// Colors for charts with improved accessibility and visual hierarchy
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFCC00', '#FF6666', '#5470C6', '#91CC75'
];

// Color scheme for source performance
const SOURCE_COLORS = {
  high: '#00C49F',    // Good ROI (green)
  medium: '#FFBB28',  // Medium ROI (yellow)
  low: '#FF8042',     // Low ROI (orange)
  negative: '#FF6666' // Negative ROI (red)
};

interface MarketingReportProps {
  data: ReportData;
  isLoading?: boolean;
  onExportPDF?: () => void;
  onFilter?: (filters: any) => void;
  hideHeader?: boolean; // Option to hide the header for embedded reports
}

const CCMarketingReport: React.FC<MarketingReportProps> = ({
  data,
  isLoading = false,
  onExportPDF,
  onFilter,
  hideHeader = false
}) => {
  // State for interactive elements
  const [activePieIndex, setActivePieIndex] = useState<number | null>(null);
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);
  const [activeSourceIndex, setActiveSourceIndex] = useState<number | null>(null);
  const [filterDate, setFilterDate] = useState<{start?: string, end?: string}>({});
  const [filterSource, setFilterSource] = useState<string | null>(null);

  // Active element handlers for enhanced interactivity
  const handlePieEnter = (_: any, index: number) => {
    setActivePieIndex(index);
  };

  const handlePieLeave = () => {
    setActivePieIndex(null);
  };

  const handleBarEnter = (_: any, index: number) => {
    setActiveBarIndex(index);
  };

  const handleBarLeave = () => {
    setActiveBarIndex(null);
  };

  const handleSourceClick = (data: any, index: number) => {
    setActiveSourceIndex(index === activeSourceIndex ? null : index);
    if (onFilter && data && data.activePayload && data.activePayload[0]) {
      const source = data.activePayload[0].payload.source;
      setFilterSource(source);
      onFilter({ source });
    }
  };

  // Custom active shape for pie chart for better interactivity
  const renderActiveShape = (props: any) => {
    const {
      cx, cy, innerRadius, outerRadius, startAngle, endAngle,
      fill, payload, percent, value
    } = props;

    return (
      <g>
        <text x={cx} y={cy} dy={-20} textAnchor="middle" fill={fill} className="text-lg font-semibold">
          {payload.trade}
        </text>
        <text x={cx} y={cy} dy={10} textAnchor="middle" fill="#333" className="text-base">
          {formatCurrency(value)}
        </text>
        <text x={cx} y={cy} dy={30} textAnchor="middle" fill="#999" className="text-sm">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius - 6}
          outerRadius={innerRadius - 2}
          fill={fill}
        />
      </g>
    );
  };

  // Custom tooltip formatter for better data display
  const customTooltipFormatter = (value: any, name: string) => {
    if (name === 'totalSales' || name === 'grossProfit' || name === 'adSpend' ||
        name === 'netProfit' || name === 'spend') {
      return [formatCurrency(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    if (name === 'roi') {
      return [`${value.toFixed(2)}x`, 'ROI'];
    }

    if (name.includes('%') || name === 'grossMarginPct') {
      return [formatPercentage(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    return [value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
  };

  // Function to apply color based on ROI performance
  const getROIColor = (roi: number) => {
    if (roi <= 0) return SOURCE_COLORS.negative;
    if (roi < 1) return SOURCE_COLORS.low;
    if (roi < 3) return SOURCE_COLORS.medium;
    return SOURCE_COLORS.high;
  };

  // Create date filter component
  const handleDateFilter = (e: React.FormEvent) => {
    e.preventDefault();
    if (onFilter && (filterDate.start || filterDate.end)) {
      onFilter({
        ...filterDate,
        source: filterSource
      });
    }
  };

  // Date filter component
  const DateFilterComponent = () => (
    <form onSubmit={handleDateFilter} className="flex items-center space-x-2 mb-4">
      <div>
        <label htmlFor="startDate" className="block text-sm text-gray-600">Start Date</label>
        <input
          type="date"
          id="startDate"
          className="border rounded px-2 py-1 text-sm"
          value={filterDate.start || ''}
          onChange={(e) => setFilterDate(prev => ({ ...prev, start: e.target.value }))}
        />
      </div>
      <div>
        <label htmlFor="endDate" className="block text-sm text-gray-600">End Date</label>
        <input
          type="date"
          id="endDate"
          className="border rounded px-2 py-1 text-sm"
          value={filterDate.end || ''}
          onChange={(e) => setFilterDate(prev => ({ ...prev, end: e.target.value }))}
        />
      </div>
      <button
        type="submit"
        className="mt-5 bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm"
      >
        Apply Filter
      </button>
      {(filterDate.start || filterDate.end || filterSource) && (
        <button
          type="button"
          className="mt-5 bg-gray-200 hover:bg-gray-300 text-gray-800 py-1 px-3 rounded text-sm"
          onClick={() => {
            setFilterDate({});
            setFilterSource(null);
            setActiveSourceIndex(null);
            if (onFilter) onFilter({});
          }}
        >
          Clear Filters
        </button>
      )}
    </form>
  );

  // Calculate combined metrics for lead source performance
  const sourcePerformance = useMemo(() => {
    // Safety check for data
    if (!data?.adSpendBySource || !Array.isArray(data.adSpendBySource)) {
      return [];
    }
    
    // Create a Map of all sales sources for quick lookup
    const salesMap = new Map();
    (data.salesBySource || []).forEach(salesItem => {
      const standardizedSource = standardizeSourceName(salesItem.source);
      salesMap.set(standardizedSource, salesItem);
    });
    
    // Start with all sources that have ad spend and include sales data where available
    return data.adSpendBySource.map(adSpendItem => {
      const standardizedAdSource = standardizeSourceName(adSpendItem.source);
      const salesItem = salesMap.get(standardizedAdSource);
      
      // If we have sales data for this source, use it; otherwise use defaults
      const totalSales = salesItem ? salesItem.totalSales : 0;
      const count = salesItem ? salesItem.count : 0;
      const grossProfit = salesItem ? salesItem.grossProfit : 0;
      
      return {
        source: adSpendItem.source,
        totalSales: totalSales,
        count: count,
        grossProfit: grossProfit,
        adSpend: adSpendItem.spend,
        roi: grossProfit / (adSpendItem.spend || 1), // Avoid division by zero
        costPerSale: count > 0 ? adSpendItem.spend / count : adSpendItem.spend,
        costPerDollar: totalSales > 0 ? adSpendItem.spend / totalSales : 1
      };
    });
  }, [data?.salesBySource, data?.adSpendBySource]);
  
  // Sort sources by ROI
  const sortedSourcePerformance = useMemo(() => {
    if (!sourcePerformance || !sourcePerformance.length) {
      return [];
    }
    return [...sourcePerformance].sort((a, b) => b.roi - a.roi);
  }, [sourcePerformance]);
  
  // Calculate average values
  const averages = useMemo(() => {
    if (!data?.salesBySource || !data?.kpis) {
      return {
        avgSaleValue: 0,
        avgGrossProfit: 0,
        avgGrossProfitMargin: 0
      };
    }
    
    const totalJobs = (data.salesBySource || []).reduce((sum, item) => sum + (item.count || 0), 0);
    return {
      avgSaleValue: totalJobs > 0 ? (data.kpis.totalSales || 0) / totalJobs : 0,
      avgGrossProfit: totalJobs > 0 ? (data.kpis.grossProfit || 0) / totalJobs : 0,
      avgGrossProfitMargin: (data.kpis.totalSales || 0) > 0 ? (data.kpis.grossProfit || 0) / data.kpis.totalSales : 0
    };
  }, [data?.kpis, data?.salesBySource]);
  
  // Group job data by trade
  const jobsByTrade = useMemo(() => {
    if (!data?.jobData || !Array.isArray(data.jobData)) {
      return {
        "Roofing": [],
        "Windows & Doors": [],
        "Other": []
      };
    }
    
    return {
      "Roofing": (data.jobData || []).filter(job => job.trade === "Roofing"),
      "Windows & Doors": (data.jobData || []).filter(job => job.trade === "Windows & Doors"),
      "Other": (data.jobData || []).filter(job => job.trade !== "Roofing" && job.trade !== "Windows & Doors")
    };
  }, [data?.jobData]);

  // Format month name or date range for YTD
  const monthName = useMemo(() => {
    if (!data?.period) return "Report";

    // Check if this is a YTD report
    if (data.period.ytd && data.period.startMonth && data.period.endMonth) {
      try {
        const startMonth = new Date(2000, data.period.startMonth - 1)
          .toLocaleString('default', { month: 'long' });
        const endMonth = new Date(2000, data.period.endMonth - 1)
          .toLocaleString('default', { month: 'long' });
        return `${startMonth}-${endMonth}`;
      } catch (error) {
        console.error('Error formatting YTD date range:', error);
        return "YTD Report";
      }
    }

    // Regular monthly report
    if (data.period.month) {
      try {
        return new Date(2000, data.period.month - 1)
          .toLocaleString('default', { month: 'long' });
      } catch (error) {
        console.error('Error formatting month:', error);
        return "Report";
      }
    }

    return "Report";
  }, [data?.period]);
  
  // Safety check for data - only after all hooks are defined
  if (!data) {
    return (
      <div className="bg-white p-6 font-sans">
        <div className="p-6 bg-red-50 text-red-700 rounded-lg">
          <h3 className="text-xl font-bold mb-2">No Data Available</h3>
          <p>This report has no data to display. Please try uploading new data files.</p>
        </div>
      </div>
    );
  }

  // Loading UI
  if (isLoading) {
    return (
      <div className="bg-white p-6 font-sans">
        <div className="flex flex-col items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-xl text-gray-600">Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <MobileReportOrientationManager>
      <div className="bg-white p-6 font-sans">
      {!hideHeader && (
        <div className="flex justify-between items-center mb-6">
          <div className="text-center flex-grow">
            <img
              src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png"
              alt="ROI Exteriors Logo"
              className="mx-auto h-20 mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-800">Cody Cline Sales Performance Report</h1>
            <h2 className="text-xl text-gray-600">
              {data.period ? `${monthName} ${data.period.year}` : "Period Report"}
            </h2>
          </div>

          {/* Report actions */}
          <div className="flex flex-col space-y-2">
            {onExportPDF && (
              <button
                onClick={onExportPDF}
                className="print-button bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                Export PDF
              </button>
            )}

          </div>
        </div>
      )}

      {/* Filter controls */}
      {onFilter && <DateFilterComponent />}

      {filterSource && (
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <p className="text-blue-700 font-medium">
            Filtered by source: <span className="font-bold">{filterSource}</span>
            <button
              onClick={() => {
                setFilterSource(null);
                if (onFilter) onFilter({ ...filterDate });
              }}
              className="ml-2 text-sm text-blue-700 hover:text-blue-900"
            >
              ×
            </button>
          </p>
        </div>
      )}
      
      {/* KPI Overview Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Key Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Total Sales</div>
            <div className="text-2xl font-bold text-blue-800">{formatCurrency(data.kpis?.totalSales || 0)}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Gross Profit</div>
            <div className="text-2xl font-bold text-green-800">{formatCurrency(data.kpis?.grossProfit || 0)}</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Expected Commission Payout</div>
            <div className="text-2xl font-bold text-orange-800">{formatCurrency(data.kpis?.salesCommissions || 0)}</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Net Profit</div>
            <div className="text-2xl font-bold text-purple-800">{formatCurrency(data.kpis?.netProfit || 0)}</div>
          </div>
        </div>
        <div className="mt-4 text-center">
          <div className="text-lg font-semibold">Net Profit Margin: <span className="text-blue-700">{formatPercentage(data.kpis?.netProfitPercent || 0)}</span></div>
          <div className="text-lg font-semibold">Gross Profit Margin: <span className="text-green-700">{formatPercentage(data.kpis?.grossProfitPercent || 0)}</span></div>
        </div>
      </div>
      
      {/* Trade Categories Performance */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Sales by Trade Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.salesByTrade || []}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                onMouseMove={handleBarEnter}
                onMouseLeave={handleBarLeave}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="trade" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar
                  dataKey="totalSales"
                  name="Sales"
                  fill="#0088FE"
                  animationDuration={750}
                  animationBegin={0}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="grossProfit"
                  name="Gross Profit"
                  fill="#00C49F"
                  animationDuration={750}
                  animationBegin={250}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.salesByTrade}
                  dataKey="totalSales"
                  nameKey="trade"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={activePieIndex !== null ? 40 : 0}
                  activeIndex={activePieIndex === null ? undefined : activePieIndex}
                  activeShape={renderActiveShape}
                  onMouseEnter={handlePieEnter}
                  onMouseLeave={handlePieLeave}
                  animationBegin={0}
                  animationDuration={800}
                >
                  {data.salesByTrade.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke={activePieIndex === index ? '#fff' : 'none'}
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    display: activePieIndex !== null ? 'none' : 'block'
                  }}
                />
                <Legend
                  formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                  iconSize={10}
                  wrapperStyle={{ paddingTop: 20 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 text-left">Trade Category</th>
                  <th className="py-2 px-4 text-right">Sales Count</th>
                  <th className="py-2 px-4 text-right">Total Sales</th>
                  <th className="py-2 px-4 text-right">Gross Profit</th>
                  <th className="py-2 px-4 text-right">Avg Sale Value</th>
                  <th className="py-2 px-4 text-right">Gross Margin %</th>
                </tr>
              </thead>
              <tbody>
                {data.salesByTrade.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4">{item.trade}</td>
                    <td className="py-2 px-4 text-right">{item.count}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.totalSales)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.grossProfit)}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.totalSales / item.count)}</td>
                    <td className="py-2 px-4 text-right">{formatPercentage(item.grossProfit / item.totalSales)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-200 font-bold">
                  <td className="py-2 px-4">TOTAL</td>
                  <td className="py-2 px-4 text-right">{data.salesByTrade.reduce((sum, item) => sum + item.count, 0)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(data.kpis.totalSales)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(data.kpis.grossProfit)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(averages.avgSaleValue)}</td>
                  <td className="py-2 px-4 text-right">{formatPercentage(averages.avgGrossProfitMargin)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Lead Source Performance */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Lead Source Performance</h2>
        <div className="mb-8">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={sortedSourcePerformance}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                onClick={handleSourceClick}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="source"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(value) => formatCurrency(value)}
                  label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, Math.max(5, ...sortedSourcePerformance.map(s => s.roi) as number[]) + 1]}
                  label={{ value: 'Profit Ratio', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  formatter={customTooltipFormatter}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '5px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                  cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                />
                <Legend
                  verticalAlign="top"
                  wrapperStyle={{ paddingBottom: 10 }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="totalSales"
                  name="Sales"
                  fill="#0088FE"
                  animationDuration={750}
                  animationBegin={0}
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="roi"
                  name="Profit Ratio"
                  stroke="#8884d8"
                  strokeWidth={3}
                  dot={(props: any) => {
                    const { cx, cy, payload } = props;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={payload.roi > 3 ? 8 : payload.roi > 1 ? 6 : 4}
                        fill={getROIColor(payload.roi)}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    );
                  }}
                  activeDot={{ r: 8, stroke: '#8884d8', strokeWidth: 3, fill: '#fff' }}
                  animationDuration={1000}
                  animationBegin={500}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-center mt-2 space-x-6 mb-4">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.high }}></div>
              <span className="text-sm">High Profit (&gt;3x)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.medium }}></div>
              <span className="text-sm">Medium Profit (1-3x)</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-2" style={{ backgroundColor: SOURCE_COLORS.low }}></div>
              <span className="text-sm">Low Profit (&lt;1x)</span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 text-left">Lead Source</th>
                <th className="py-2 px-4 text-right">Sales Count</th>
                <th className="py-2 px-4 text-right">Total Sales</th>
                <th className="py-2 px-4 text-right">Gross Profit</th>
                <th className="py-2 px-4 text-right">Profit Ratio</th>
              </tr>
            </thead>
            <tbody>
              {sortedSourcePerformance.map((source, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-2 px-4">{source.source}</td>
                  <td className="py-2 px-4 text-right">{source.count}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.totalSales)}</td>
                  <td className="py-2 px-4 text-right">{formatCurrency(source.grossProfit)}</td>
                  <td className="py-2 px-4 text-right">{source.roi.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Executive Summary */}
      <div className="mb-12 p-6 bg-gray-50 rounded-lg">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Executive Summary</h2>
        <div className="space-y-3">
          <p>
            For {monthName} {data.period?.year}, Cody Cline achieved <strong>{formatCurrency(data.kpis.totalSales)}</strong> in total sales with 
            a gross profit of <strong>{formatCurrency(data.kpis.grossProfit)}</strong>. With no marketing spend due to the referral-based nature
            of these sales, and after accounting for company overhead (<strong>{formatCurrency(data.kpis.companyOH)}</strong>),
            the net profit comes to <strong>{formatCurrency(data.kpis.netProfit)}</strong> and a net profit margin of 
            <strong> {formatPercentage(data.kpis.netProfitPercent)}</strong>. Cody&apos;s 50% share of this profit is <strong>{formatCurrency(data.kpis.netProfit / 2)}</strong>.
          </p>
          
          <p>
            <strong>Lead Source Analysis:</strong> All sales came through referrals, with 
            {sortedSourcePerformance.length > 0 ? ` ${sortedSourcePerformance[0].source}` : ' referral networks'} 
            being the primary source at <strong>{formatCurrency(sortedSourcePerformance[0]?.totalSales || 0)}</strong>, 
            followed by {sortedSourcePerformance.length > 1 ? sortedSourcePerformance[1].source : 'other sources'} at 
            <strong> {formatCurrency(sortedSourcePerformance[1]?.totalSales || 0)}</strong>.
          </p>
          
          <p>
            <strong>Trade Category Performance:</strong> The leading trade category was 
            <strong> {data.salesByTrade[0]?.trade || 'Windows & Doors'}</strong> at <strong>{formatCurrency(data.salesByTrade[0]?.totalSales || 0)}</strong>, 
            followed by <strong>{data.salesByTrade[1]?.trade || 'Gutters'}</strong> at <strong>{formatCurrency(data.salesByTrade[1]?.totalSales || 0)}</strong>.
            The gross profit margins are excellent, with {data.salesByTrade[0]?.trade || 'Windows & Doors'} generating
            <strong> {formatPercentage((data.salesByTrade[0]?.grossProfit || 0) / (data.salesByTrade[0]?.totalSales || 1))}</strong> gross margin.
          </p>
          
          <p>
            <strong>Recommendations:</strong> 
            {` Continue leveraging Cody's referral network, as it demonstrates excellent profitability without requiring marketing spend. `}
            {` Consider implementing a referral rewards program to further incentivize these highly profitable leads.`}
            {` Focus on the ${
              [...data.salesByTrade].sort((a, b) => (b.grossProfit/b.totalSales) - (a.grossProfit/a.totalSales))[0]?.trade || 'Windows & Doors'
            } category which has the highest profit margin.`}
          </p>
        </div>
      </div>
      
      {/* Individual Job Analysis */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Individual Job Analysis</h2>
        
        {/* Roofing Jobs */}
        {jobsByTrade["Roofing"].length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold py-2 px-4 bg-blue-50 rounded mb-4">
              Roofing Jobs ({jobsByTrade["Roofing"].length} jobs, {formatCurrency(jobsByTrade["Roofing"].reduce((sum, job) => sum + job.contractTotal, 0))} total revenue)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-4 text-left">Job Info</th>
                    <th className="py-2 px-4 text-left">Lead Source</th>
                    <th className="py-2 px-4 text-right">Contract Total</th>
                    <th className="py-2 px-4 text-right">Gross Margin %</th>
                    <th className="py-2 px-4 text-right">Gross Profit</th>
                    <th className="py-2 px-4 text-right">Cody&apos;s Share (50%)</th>
                    <th className="py-2 px-4 text-right">Net Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsByTrade["Roofing"].map((job, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4">{job.jobInfo}</td>
                      <td className="py-2 px-4">{job.leadSource}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                      <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit / 2)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Windows & Doors Jobs */}
        {jobsByTrade["Windows & Doors"].length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold py-2 px-4 bg-green-50 rounded mb-4">
              Windows & Doors Jobs ({jobsByTrade["Windows & Doors"].length} jobs, {formatCurrency(jobsByTrade["Windows & Doors"].reduce((sum, job) => sum + job.contractTotal, 0))} total revenue)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-4 text-left">Job Info</th>
                    <th className="py-2 px-4 text-left">Lead Source</th>
                    <th className="py-2 px-4 text-right">Contract Total</th>
                    <th className="py-2 px-4 text-right">Gross Margin %</th>
                    <th className="py-2 px-4 text-right">Gross Profit</th>
                    <th className="py-2 px-4 text-right">Cody&apos;s Share (50%)</th>
                    <th className="py-2 px-4 text-right">Net Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsByTrade["Windows & Doors"].map((job, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4">{job.jobInfo}</td>
                      <td className="py-2 px-4">{job.leadSource}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                      <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit / 2)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Other Jobs */}
        {jobsByTrade["Other"].length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold py-2 px-4 bg-orange-50 rounded mb-4">
              Other Jobs ({jobsByTrade["Other"].length} jobs, {formatCurrency(jobsByTrade["Other"].reduce((sum, job) => sum + job.contractTotal, 0))} total revenue)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-4 text-left">Job Info</th>
                    <th className="py-2 px-4 text-left">Lead Source</th>
                    <th className="py-2 px-4 text-left">Trade</th>
                    <th className="py-2 px-4 text-right">Contract Total</th>
                    <th className="py-2 px-4 text-right">Gross Margin %</th>
                    <th className="py-2 px-4 text-right">Gross Profit</th>
                    <th className="py-2 px-4 text-right">Cody&apos;s Share (50%)</th>
                    <th className="py-2 px-4 text-right">Net Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsByTrade["Other"].map((job, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4">{job.jobInfo}</td>
                      <td className="py-2 px-4">{job.leadSource}</td>
                      <td className="py-2 px-4">{job.trade}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.contractTotal)}</td>
                      <td className="py-2 px-4 text-right">{formatPercentage(job.grossMarginPct)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.grossProfit)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit / 2)}</td>
                      <td className="py-2 px-4 text-right">{formatCurrency(job.netProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-semibold mb-3">Individual Job Insights</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Highest Value Jobs:</strong> {data.jobData.sort((a, b) => b.contractTotal - a.contractTotal)[0]?.jobInfo || "No jobs found"} (${data.jobData.sort((a, b) => b.contractTotal - a.contractTotal)[0]?.contractTotal.toFixed(2) || "0.00"})</li>
            <li><strong>Best Profit Margins:</strong> {data.jobData.sort((a, b) => b.grossMarginPct - a.grossMarginPct)[0]?.jobInfo || "No jobs found"} ({formatPercentage(data.jobData.sort((a, b) => b.grossMarginPct - a.grossMarginPct)[0]?.grossMarginPct || 0)})</li>
            <li><strong>Referral Network Value:</strong> {data.salesBySource[0]?.source || "Cody&apos;s network"} delivered {data.salesBySource[0]?.count || 0} sales totaling {formatCurrency(data.salesBySource[0]?.totalSales || 0)}</li>
            <li><strong>Key Observations:</strong> The referral network provides high-value jobs with excellent margins, demonstrating the value of relationship-based business development.</li>
          </ul>
        </div>
      </div>
      
      <div className="text-center text-gray-600 text-sm mt-8">
        <p>Generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        <p>ROI Exteriors Sales Performance Report</p>
      </div>
    </div>
    </MobileReportOrientationManager>
  );
};

export default CCMarketingReport;